// ============================================================================
// PatientEcgTab — «Исследования ЭКГ» кабинета пациента (ТОЛЬКО ПРОСМОТР).
//
// Данные — существующий справочник cardio_studies: история по карточке пациента
// либо СВОИ исследования (владелец записи, OwnScope). Права — SSoT сервера:
// нет права «Чтение» — явное ограничение, а не пустая история.
//
// Карточка исследования открывается в EcgStudyDetailDialog с readOnly: снимок,
// измерения, вероятностные находки и заключение врача читаются, но НЕ пишутся.
// ============================================================================

import { useState } from 'react';
import { Activity } from 'lucide-react';
import { EcgStudyDetailDialog } from '@/components/ecg/EcgStudyDetailDialog';
import { PatientCardEcgSection } from '@/components/medic/PatientCardEcgSection';
import { SectionState } from '@/components/medic/PatientCardStates';
import { emptyRange, type DateRange } from '@/components/medic/patientCardData';
import type { CabinetEcg } from '@/lib/patientCabinetDashboard';

interface Props {
    data: CabinetEcg;
    onRefresh: () => void;
}

export function PatientEcgTab({ data, onRefresh }: Props) {
    const [range, setRange] = useState<DateRange>(emptyRange);
    const [studyId, setStudyId] = useState('');

    if (data.state !== 'ready') {
        return (
            <SectionState
                title={
                    data.state === 'denied'
                        ? 'История ЭКГ недоступна'
                        : data.state === 'error'
                            ? 'Не удалось загрузить историю ЭКГ'
                            : 'Модуль ЭКГ не установлен'
                }
                text={data.error || data.note}
                tone={data.state === 'error' ? 'danger' : data.state === 'denied' ? 'warning' : 'muted'}
                onRetry={onRefresh}
            />
        );
    }

    return (
        <>
            <div className="mb-2 flex items-center gap-2 rounded-lg border bg-card px-3 py-2 text-xs text-muted-foreground">
                <Activity className="h-3.5 w-3.5 text-primary" />
                <span>{data.note || 'История ваших исследований ЭКГ.'}</span>
                <span className="ml-auto text-[10px]">Только просмотр</span>
            </div>
            <PatientCardEcgSection
                data={data}
                range={range}
                onRangeChange={setRange}
                onReload={onRefresh}
                onOpenStudy={setStudyId}
            />
            <EcgStudyDetailDialog
                studyId={studyId}
                onClose={() => setStudyId('')}
                onOpenPatient={() => setStudyId('')}
                readOnly
            />
        </>
    );
}
